// Composables
import { createRouter, createWebHistory, START_LOCATION, type NavigationGuard } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { useSubdomainStore } from '@/store/subdomain'
import { useUserStore } from "@/store/user"
import { ensureSessionLoaded } from '@/utils/ensureSessionLoaded'
import { isSessionFailure } from '@/utils/sessionErrors'
import { consumeHandoffAuthMethod, consumeSessionHandoff } from '@/utils/sessionHandoff'
import { applyOriginCognitoPool } from '@/utils/cognitoPoolConfig'
import { getAuth0Client } from '@/utils/auth0Client'
import { useSocietyStore } from "@/store/society"
import { FEATURES, checkFeatureFlag, type FeatureKey } from '@/features'
import { resolveInvoicesView } from '@/views/payments/resolveInvoicesView'
import { resolveRequestsView } from '@/views/requests/resolveRequestsView'
import { resolveProvidersView } from '@/views/providers/resolveProvidersView'
import { useImmersiveView } from '@/composables/useImmersiveView'
import { canSeeHome } from '@/utils/canSeeHome'
import { rememberReturnPath } from '@/utils/returnPath'
import { isConnectorLoginHost, isConnectorLoginPath } from '@/utils/connectorLoginHost'
import { canSeeAnalytics } from '@/utils/canSeeAnalytics'
import { canSeeProviderDetail } from '@/utils/canSeeProviderDetail'

export const configGuard = (_to: any, _from: any, next: any) => {
  const userStore = useUserStore()
  if (userStore.canManageConfig) next()
  else next('/')
}

export const operationsGuard = (_to: any, _from: any, next: any) => {
  const userStore = useUserStore()
  if (userStore.canAccessOperations) next()
  else next('/')
}

// Starts the policies fetch alongside the route transition instead of waiting for
// RenditionForm.vue to mount, so the policy step's autocomplete is rarely seen loading.
//
// The store MUST stay a dynamic import: statically importing it here pulls `utils/api.ts` into the
// router's eager graph (via utils/vueform_schema), and that module calls `useSocietyStore()` at
// top level — which runs before `app.use(pinia)` and blanks the whole app.
export const prefetchRenditionPolicies = (_to: any, _from: any, next: any) => {
  void import('@/store/rendition').then(({ useRenditionStore }) => useRenditionStore().loadPolicies())
  next()
}

/**
 * For screens that host both configuration and operational content. Invoice settings is one: five
 * config tabs plus the Argus history, which the backend classifies as an operation. Either axis
 * gets in and the tabs themselves are filtered by role, so a config-only role keeps its screens and
 * an operations-only role reaches the history without gaining the flow builder.
 */
export const configOrOperationsGuard = (_to: any, _from: any, next: any) => {
  const userStore = useUserStore()
  if (userStore.canManageConfig || userStore.canAccessOperations) next()
  else next('/')
}

export const budgetGuard = (_to: any, _from: any, next: any) => {
  const userStore = useUserStore()
  const societyStore = useSocietyStore()
  if (userStore.canAccessOperations && societyStore.hasBudget) next()
  else next('/')
}

// Budget DETAIL is reachable by any user of a society with the module: approvers open it from the
// request ("Ver detalle") and are the budget owners, but need not have an operations role.
export const budgetDetailGuard = (_to: any, _from: any, next: any) => {
  const societyStore = useSocietyStore()
  if (societyStore.hasBudget) next()
  else next('/')
}

// Both halves are load-bearing: the endpoints answer 403 without the operations permission, and a
// society with the rule off must see no trace of the feature (the ticket's no-spill criterion).
export const paymentComplementsGuard: NavigationGuard = (_to, _from, next) => {
  const userStore = useUserStore()
  if (userStore.canAccessOperations && checkFeatureFlag(FEATURES.PAYMENT_COMPLEMENTS)) next()
  else next('/')
}

export const providerDirectoryGuard = (_to: any, _from: any, next: any) => {
  if (canSeeProviderDetail()) next()
  else next('/providers/list')
}

export const homeLandingGuard: NavigationGuard = (to, from) => {
  const entering = from === START_LOCATION || from.path.startsWith('/auth/')
  if (!entering || to.path !== '/' || to.hash || Object.keys(to.query).length > 0) return true
  if (!canSeeHome()) return true
  return { path: '/home', replace: true }
}

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/default/Default.vue'),
    children: [
      {
        path: 'purchases',
        children: [
          {
            path: "history",
            name: "Quotations History",
            component: () => import('@/views/requests/QuotationHistory.vue'),
            beforeEnter: operationsGuard
          }
        ]
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/HomeView.vue'),
        meta: { featureFlag: FEATURES.NEW_AP_VIEW },
        beforeEnter: () => (canSeeHome() ? true : { path: '/', replace: true }),
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/analytics/AnalyticsView.vue'),
        meta: { featureFlag: FEATURES.NEW_AP_VIEW },
        beforeEnter: () => (canSeeAnalytics() ? true : { path: '/', replace: true }),
      },
      {
        path: '',
        children: [
          {
            path: '',
            name: 'Purchase Requests',
            component: resolveRequestsView,
          },
          {
            path: 'requests/following',
            name: 'Requests Following',
            component: () => import('@/views/requests/RequestsFollowing.vue'),
          },
          {
            path: 'requests/due-soon',
            name: 'Requests Due Soon',
            component: () => import('@/views/requests/RequestsDueSoon.vue'),
          },
          {
            path: 'requests/groups/:groupId',
            name: 'Request Group Details',
            component: () => import('@/views/requests/group/RequestGroupDetails.vue'),
          },
          {
            path: 'requests/:id',
            name: 'Purchase Request Details',
            component: () => import('@/views/requests/RequestDetails.vue'),
          },
          {
            path: 'renditions/create',
            name: 'Rendition Create',
            component: () => import('@/views/renditions/RenditionCreate.vue'),
            beforeEnter: prefetchRenditionPolicies,
          },
          {
            path: 'renditions/:id/edit',
            name: 'Rendition Edit',
            component: () => import('@/views/renditions/RenditionCreate.vue'),
            props: true,
            beforeEnter: prefetchRenditionPolicies,
          },
          {
            path: 'my-invoices',
            name: 'My Files',
            // Not gated by rendiciones: "Mis Archivos" is a general archivos surface (other
            // societies use it for payment requests too). New-UX access is via Sidebar.vue.
            component: () => import('@/views/myInvoices/MyInvoices.vue'),
          },
        ]
      },
      {
        path: 'payments',
        children: [
          {
            path: "",
            name: "Payments",
            component: () => import('@/views/payments/Payments.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: ":id",
            name: "Payment Details",
            component: () => import('@/views/payments/PaymentDetails.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: "purchase_orders",
            name: "Purchase Orders",
            component: () => import('@/views/payments/PurchaseOrders.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: "invoices",
            name: "Documents",
            component: resolveInvoicesView,
            beforeEnter: operationsGuard
          },
          {
            path: "invoices/create",
            name: "Invoice Form",
            component: () => import('@/views/payments/InvoiceForm.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: "invoices/settings",
            name: "Invoice Settings",
            component: () => import('@/views/payments/InvoiceSettings.vue'),
            beforeEnter: configOrOperationsGuard
          },
          {
            path: "invoices/inbox",
            name: "Invoices Inbox",
            component: () => import('@/views/payments/Inbox.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: "credit_cards",
            name: "Credit Cards",
            component: () => import('@/views/payments/CreditCards.vue'),
            beforeEnter: operationsGuard
          },
          {
            path: "receptions",
            name: "Receptions",
            component: () => import('@/views/payments/Receptions.vue'),
            beforeEnter: operationsGuard
          },
        ]
      },
      {
        path: "transactions",
        name: "Transactions",
        component: () => import('@/views/payments/Transactions.vue'),

      },
      {
        path: 'bank-batches',
        children: [
          {
            path: "",
            name: "Bank Batches",
            component: () => import('@/views/bank-batches/BankBatchesView.vue'),
            beforeEnter: operationsGuard,
          },
          {
            path: ":id",
            name: "Bank Batch Details",
            component: () => import('@/views/bank-batches/BankBatchDetailsView.vue'),
            beforeEnter: operationsGuard,
            props: true
          }
        ]
      },
      {
        path: 'contracts',
        children: [
          {
            path: "",
            name: "Contracts",
            component: () => import('@/views/contracts/ContractsView.vue'),
          },
          {
            path: "create",
            name: "Contract Form",
            component: () => import('@/views/contracts/ContractForm.vue'),
            beforeEnter: operationsGuard,
          },
          {
            path: ":id",
            name: "Contract Details",
            component: () => import('@/views/contracts/ContractDetails.vue'),
            props: true
          },
        ]
      },
      {
        path: 'budgets',
        children: [
          {
            path: "",
            name: "Budgets",
            component: () => import('@/views/budgets/BudgetsView.vue'),
            beforeEnter: budgetGuard
          },
          {
            path: ":id",
            name: "Budget Details",
            component: () => import('@/views/budgets/BudgetDetails.vue'),
            beforeEnter: budgetDetailGuard
          }
        ]
      },
      {
        path: 'providers',
        children: [
          {
            path: 'list',
            name: 'Providers',
            component: resolveProvidersView,
          },
          {
            path: ':id',
            name: 'Provider Detail',
            component: () => import('@/views/providers/Provider.vue'),
            beforeEnter: providerDirectoryGuard,
          },
          {
            path: "risk",
            name: "Provider Risk",
            component: () => import('@/views/providers/ProviderRisk.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "payment-complements",
            name: "Payment Complements",
            component: () => import('@/views/providers/PaymentComplements.vue'),
            beforeEnter: paymentComplementsGuard,
          },
          {
            path: "evaluation",
            name: "ProviderEvaluation",
            component: () => import('@/views/providers/ProviderEvaluation.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "flows",
            name: "Provider Flows",
            component: () => import('@/views/providers/ProviderFlows.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "forms",
            children: [
              {
                path: "",
                name: "Provider Forms",
                component: () => import('@/views/providers/Forms.vue'),
                beforeEnter: configGuard,
              },
              {
                path: "create",
                name: "Provider Form Form",
                component: () => import('@/views/providers/FormForm.vue'),
                beforeEnter: configGuard,
              },
              {
                path: ":id",
                name: "Edit Provider Form",
                component: () => import('@/views/providers/FormForm.vue'),
                beforeEnter: configGuard,
              }
            ]
          },
        ]
      },
      {
        path: 'administration',
        children: [
          {
            path: "masters",
            name: "Masters",
            component: () => import('@/views/administration/AccountMaster.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "policies",
            name: "Policies",
            component: () => import('@/views/administration/Policies.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "templates",
            name: "Templates",
            component: () => import('@/views/administration/Templates.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "users",
            name: "Users",
            component: () => import('@/views/administration/Users.vue'),
            beforeEnter: configGuard,
          },
          {
            path: "settings",
            name: "Settings",
            component: () => import('@/views/administration/Settings.vue'),
            beforeEnter: configGuard,
          },
        ]
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/Profile.vue'),
      },
      {
        path: 'upgrade_plan',
        name: 'Upgrade Plan',
        component: () => import('@/views/UpgradePlan.vue'),
      },
      {
        path: 'upload_document',
        name: 'Upload Document',
        component: () => import('@/views/payments/DocumentUpload.vue')
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('@/layouts/auth/Auth.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/Login.vue'),
      },
      {
        path: 'auth0-callback',
        name: 'Auth0 Callback',
        component: () => import('@/views/auth/Auth0Callback.vue'),
      },
      {
        path: 'reset_password',
        name: 'Reset Password',
        component: () => import('@/views/auth/ResetPassword.vue'),
      },
      {
        path: 'forgot_password',
        name: 'Forgot Password',
        component: () => import('@/views/auth/ForgotPassword.vue'),
      },
      {
        path: 'password_expired',
        name: 'Password Expired',
        component: () => import('@/views/auth/PasswordExpired.vue'),
      }
    ]
  },
  {
    path: '/invalid_tenant',
    component: () => import('@/layouts/auth/Auth.vue'),
    children: [
      {
        path: '',
        name: 'Invalid Tenant',
        component: () => import('@/views/InvalidTenant.vue'),
      },
    ]
  },
  {
    path: '/no_access',
    component: () => import('@/layouts/auth/Auth.vue'),
    children: [
      {
        path: '',
        name: 'No Access',
        component: () => import('@/views/NoAccess.vue'),
      },
    ]
  },
  {
    path: '/connect',
    component: () => import('@/layouts/auth/Auth.vue'),
    children: [
      {
        path: '',
        name: 'Connect',
        component: () => import('@/views/ConnectConsent.vue'),
      },
    ]
  },
  {
    path: '/d',
    name: 'Open Document',
    component: () => import('@/views/OpenDocument.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

// Handle chunk loading errors (catches lazy-loaded component failures after deployments)
// This only runs when an actual error occurs - zero performance impact during normal operation
router.onError((error) => {
  const errorMessage = error?.message || '';
  const errorStack = error?.stack || '';

  // Check for chunk loading error patterns (404s when old chunks are requested after deployment)
  // Must be specific to JS chunk files to avoid catching API 404s or other errors
  const isChunkError =
    /Loading chunk [\d]+ failed/i.test(errorMessage) ||
    /Failed to fetch dynamically imported module/i.test(errorMessage) ||
    /ChunkLoadError/i.test(errorMessage) ||
    (/net::ERR_ABORTED.*404/i.test(errorMessage) && /\.js/i.test(errorMessage + errorStack)) ||
    (/404.*Not Found/i.test(errorStack) && /\.js/i.test(errorStack));

  if (isChunkError) {
    // New deployment happened while user has old version open
    // Preserve intended target route across the reload
    try {
      const intended = sessionStorage.getItem('fto:intendedPath');
      const fallback = window.location.pathname + window.location.search + window.location.hash;
      const targetPath = intended || fallback;
      sessionStorage.setItem('fto:postReloadPath', targetPath);
    } catch { /* no-op */ }

    // Reload to get the new version with correct chunks
    window.location.reload();
    return;
  }

  // Re-throw other errors for normal error handling
  throw error;
});

router.beforeEach(async (to, from, next) => {
  // [CHUNK LOADING ERROR] Persist last intended route so we can restore it after a version reload
  try { sessionStorage.setItem('fto:intendedPath', to.fullPath); } catch { /* no-op */ }

  const hostname = window.location.hostname
  const tenant = hostname.includes('.finnecto.com')
    ? hostname.split('.finnecto.com')[0]
    : hostname.includes('.localhost')
      ? hostname.split('.localhost')[0]
      : null
  const userStore = useUserStore();
  const subdomainStore = useSubdomainStore();

  if (isConnectorLoginHost(hostname)) {
    if (!isConnectorLoginPath(to.path)) {
      next({ path: '/connect', query: to.query })
    } else if (to.path === '/connect' && !userStore.getEmail) {
      rememberReturnPath(to.fullPath)
      next('/auth/login')
    } else {
      next()
    }
    return
  }

  // Already heading to a terminal auth-error page — let it through, skip everything else.
  // Sin esto /no_access entra en loop consigo misma: el guard la evaluaria, no
  // encontraria sesion y la volveria a rutear.
  if (to.path.includes('/invalid_tenant') || to.path.includes('/no_access')) {
    next()
    return
  }

  // Validate the current tenant against the API (two-tier cache: Pinia → localStorage → network)
  if (tenant) {
    try {
      const isValid = await subdomainStore.checkSubdomain(tenant)
      if (!isValid) {
        // Telemetry: see .planning/2026-05-21-invalid-tenant-investigation.md
        Sentry.captureMessage('Subdomain check failed -> /invalid_tenant', {
          level: 'warning',
          tags: { failure_point: 'router_invalid_tenant_redirect', reason: 'api_said_false' },
          extra: {
            tenant,
            hostname: window.location.hostname,
            origin: window.location.origin,
            to_path: to.fullPath,
          },
        })
        next('/invalid_tenant')
        return
      }
    } catch (err) {
      // Network failure: fail-closed — treat unknown subdomain as invalid
      // Telemetry: see .planning/2026-05-21-invalid-tenant-investigation.md
      Sentry.captureMessage('Subdomain check failed -> /invalid_tenant', {
        level: 'warning',
        tags: { failure_point: 'router_invalid_tenant_redirect', reason: 'network_error' },
        extra: {
          tenant,
          hostname: window.location.hostname,
          origin: window.location.origin,
          to_path: to.fullPath,
          error_message: (err as Error)?.message,
        },
      })
      next('/invalid_tenant')
      return
    }
  }

  // Returning from an Auth0 SSO redirect: Auth0Callback.vue owns session establishment.
  // Running ensureSessionLoaded here would fall to the Cognito default (authMethod is still
  // undefined mid-callback) and send a stale Cognito token, bouncing the SSO return.
  if (to.path.includes('/auth/auth0-callback') || (to.query.code && to.query.state)) {
    next()
    return
  }

  // Ambiente switch: al aterrizar desde otro subdominio el store de este origin arranca sin
  // authMethod. Si el ambiente de origen dejo el marker, este usuario ya sabemos que metodo usa;
  // sembralo antes de ensureSessionLoaded para no defaultear a Cognito y mandar el token
  // equivocado (usuario SSO -> pantalla en blanco). El marker es de un solo uso.
  // Awaiting it also lands the redeemed Cognito keys before ensureSessionLoaded reads them.
  const redeemedAuthMethod = await consumeSessionHandoff()
  // A redeemed staff session switches Amplify to the staff pool before ensureSessionLoaded mints a token.
  if (redeemedAuthMethod === 'cognito') applyOriginCognitoPool()
  const handoffAuthMethod = redeemedAuthMethod ?? consumeHandoffAuthMethod()
  if (handoffAuthMethod && !userStore.getAuthMethod) {
    userStore.setAuthMethod(handoffAuthMethod as 'auth0' | 'cognito')

    // Cero-clic para SSO: el refresh token de Auth0 es por origin, asi que aca no hay sesion
    // local. Como finnecto.us.auth0.com comparte la sesion SSO entre subdominios, disparar el
    // redirect con la organizacion rebota solo sin pedir nada. Sin loop: el marker ya se
    // consumio, asi que si el redirect vuelve sin sesion el segundo paso cae al login normal.
    if (handoffAuthMethod === 'auth0') {
      const auth0 = getAuth0Client()
      if (auth0) {
        const organization = tenant ? subdomainStore.getOrganization(tenant) : null
        try {
          rememberReturnPath(to.fullPath)
          await auth0.loginWithRedirect({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              scope: 'openid profile email offline_access',
              ...(organization && { organization }),
            },
            appState: { target: to.fullPath },
          })
          // Aborta la navegacion SPA: la pagina se esta yendo entera a Auth0.
          next(false)
          return
        } catch {
          // cae al flujo normal (ensureSessionLoaded / login) si el redirect no arranca
        }
      }
    }
  }

  // Ensure session is loaded before checking if the user is authenticated
  try {
    await ensureSessionLoaded()
  } catch (e) {
    // 403 = la credencial es valida y este ambiente la rechaza. Es un estado terminal
    // con pantalla propia: mandarlo a login produce un loop, porque el login va a
    // volver a ser rechazado por el mismo motivo.
    if (isSessionFailure(e) && e.kind === 'environment-rejected') {
      next('/no_access')
      return
    }
    // why: sesion muerta e irrenovable. Limpiar el estado persistido para que
    // isAuthenticated no siga true por el email en localStorage; si no, el guard deja
    // la app corriendo pidiendo un token que nunca renueva y solo se destraba borrando
    // localStorage a mano. Limpio y sigo el flujo: la rama de abajo manda a /auth/login.
    if (isSessionFailure(e) && e.kind === 'no-session') {
      userStore.clearUser()
    }
    // Cualquier otra cosa sigue el flujo: el chequeo de isAuthenticated de abajo
    // manda a login si no hay sesion.
  }

  const isAuthenticated = (userStore.getEmail && userStore.getEmail !== "")

  // Trying to authenticate when authenticated
  if (to.path.includes("/auth") && !to.path.includes("password_expired") && isAuthenticated) {
    next("/")
  }
  else if (to.path.includes("/upload_document")) {
    next()
  }
  // Trying to access app when not authenticated
  else if (!to.path.includes("/auth") && !isAuthenticated) {
    rememberReturnPath(to.fullPath)
    next('/auth/login')
  }
  // Accesing app authenticated or trying to authenticate when not authenticated
  else {
    const flag = to.meta?.featureFlag as FeatureKey | undefined
    if (flag && !checkFeatureFlag(flag)) {
      next('/')
      return
    }
    next()
  }
});

router.beforeEach(homeLandingGuard)

router.afterEach((to, from) => {
  if (to.path !== from.path) useImmersiveView().reset()
});

export default router
